import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import type { TaskRecord } from '@/types/admin'
import { nowIso } from '@/utils/format'

interface NewTaskInput {
  taskType: string
  title: string
  total?: number
  success?: number
  failed?: number
  message?: string
}

let taskSequence = 100

function nowLabel(): string {
  return nowIso()
}

export const useTaskStore = defineStore('tasks', () => {
  const tasks = ref<TaskRecord[]>([])
  const timers = new Map<string, number>()

  const running = computed(() => tasks.value.filter(task => task.status === '进行中' || task.status === '排队中'))
  const recent = computed(() => tasks.value.slice(0, 18))

  function addSeedTasks(seed: Array<Record<string, unknown>>): void {
    if (tasks.value.length) return
    tasks.value = seed.map((row, index) => ({
      id: String(row.id ?? `seed-task-${index}`),
      taskNo: String(row.task_no ?? `RW${index + 1}`),
      taskType: String(row.task_type ?? '任务'),
      title: String(row.title ?? '原型任务'),
      status: row.status === 'running' ? '进行中' : row.status === 'queued' ? '排队中' : row.status === 'failed' ? '已失败' : row.status === 'partial' ? '部分失败' : '已完成',
      progress: Number(row.progress ?? 0),
      total: Number(row.total ?? 0),
      success: Number(row.success ?? 0),
      failed: Number(row.failed ?? 0),
      message: String(row.message ?? ''),
      createdAt: String(row.created_at ?? nowLabel()),
      finishedAt: String(row.finished_at ?? ''),
      retryable: row.status === 'failed' || row.status === 'partial',
    }))
  }

  function createTask(input: NewTaskInput): TaskRecord {
    taskSequence += 1
    const total = Math.max(1, input.total ?? 1)
    const task: TaskRecord = {
      id: `task-runtime-${Date.now()}-${taskSequence}`,
      taskNo: `RW2026${String(taskSequence).padStart(4, '0')}`,
      taskType: input.taskType,
      title: input.title,
      status: '进行中',
      progress: 4,
      total,
      success: input.success ?? 0,
      failed: input.failed ?? 0,
      message: input.message ?? '原型任务正在当前浏览器中执行',
      createdAt: nowLabel(),
      finishedAt: '',
      retryable: false,
    }
    tasks.value.unshift(task)
    start(task.id)
    return task
  }

  function start(id: string): void {
    if (timers.has(id)) window.clearInterval(timers.get(id))
    const timer = window.setInterval(() => {
      const task = tasks.value.find(item => item.id === id)
      if (!task) {
        window.clearInterval(timer)
        timers.delete(id)
        return
      }
      task.progress = Math.min(100, task.progress + Math.max(7, Math.round(Math.random() * 17)))
      task.success = Math.min(task.total, Math.round((task.progress / 100) * task.total))
      if (task.progress >= 100) {
        task.progress = 100
        task.status = '已完成'
        task.success = task.total
        task.finishedAt = nowLabel()
        task.message = `已完成 ${task.total} 项，结果可在当前页面查看`
        window.clearInterval(timer)
        timers.delete(id)
      }
    }, 460)
    timers.set(id, timer)
  }

  function retry(id: string): void {
    const task = tasks.value.find(item => item.id === id)
    if (!task) return
    task.status = '进行中'
    task.progress = 0
    task.success = 0
    task.failed = 0
    task.finishedAt = ''
    task.message = '已重新加入原型任务队列'
    start(id)
  }

  function cancel(id: string): void {
    const task = tasks.value.find(item => item.id === id)
    if (!task) return
    task.status = '已取消'
    task.message = '任务已取消'
    task.finishedAt = nowLabel()
    task.retryable = true
    const timer = timers.get(id)
    if (timer) window.clearInterval(timer)
    timers.delete(id)
  }

  function simulateFailure(id: string): void {
    const task = tasks.value.find(item => item.id === id)
    if (!task) return
    task.status = '部分失败'
    task.progress = 100
    task.failed = Math.max(1, Math.round(task.total * 0.12))
    task.success = Math.max(0, task.total - task.failed)
    task.message = `${task.failed} 项未完成，可重试失败项`
    task.retryable = true
    task.finishedAt = nowLabel()
    const timer = timers.get(id)
    if (timer) window.clearInterval(timer)
    timers.delete(id)
  }

  return { tasks, running, recent, addSeedTasks, createTask, retry, cancel, simulateFailure }
})
